import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { getUsersData } from '../data/users';
import { fetchUserAttributes, updateUserAttributes, fetchAvailableSites } from '../services/userAttributesService';
import { useNotification } from '../components/NotificationContext';
import { fetchAuthSession } from 'aws-amplify/auth';
import useTranslation from '../services/translationService';
import { apiPost } from '../utils/api';
import { amountFractionDigits } from '../utils/currency';
import IPAddressDetails from '../components/IPAddressDetails';
import DeviceInfoPanel, { deriveDeviceSummary } from '../components/DeviceInfoPanel';
import TransactionModal from '../components/TransactionModal';
import ChangePasswordModal from '../components/ChangePasswordModal';
import { isUserRestricted } from '../services/breakglassService';
import { 
  UserIcon, 
  CalendarIcon,
  LockClosedIcon,
  UserGroupIcon,
  CurrencyDollarIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  ArrowTopRightOnSquareIcon,
  ShieldCheckIcon,
  BanknotesIcon,
  BuildingOfficeIcon,
  GlobeAsiaAustraliaIcon,
  BuildingStorefrontIcon,
  StarIcon,
  ArrowLeftIcon,
  ChartBarIcon,
  DocumentTextIcon,
  CreditCardIcon,
  ArrowsRightLeftIcon,
  WalletIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  KeyIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  GlobeAltIcon
} from '@heroicons/react/24/outline';

// Constants from AgentTree
const USER_TYPES = {
  ADMIN: 'admin',
  COUNTRY_ADMIN: 'country',
  CITY_ADMIN: 'city',
  SUPER_AGENT: 'super-agent',
  AGENT: 'agent',
  PLAYER: 'player'
};

// Currency sort order
const CURRENCY_ORDER = ['LBP', 'USD', 'TND', 'TRY', 'IQD', 'KWD'];

// Currencies that are mutually exclusive for players.
// A player may hold at most ONE of these at a time, optionally combined with LBP.
const EXCLUSIVE_PLAYER_CURRENCIES = ['USD', 'TND', 'TRY', 'IQD', 'KWD'];

const sortCurrencies = (entries) => {
  return [...entries].sort(([currencyA], [currencyB]) => {
    const indexA = CURRENCY_ORDER.indexOf(currencyA);
    const indexB = CURRENCY_ORDER.indexOf(currencyB);
    
    // If both are in the order array, sort by their position
    if (indexA !== -1 && indexB !== -1) {
      return indexA - indexB;
    }
    // If only A is in the array, A comes first
    if (indexA !== -1) return -1;
    // If only B is in the array, B comes first
    if (indexB !== -1) return 1;
    // If neither is in the array, sort alphabetically
    return currencyA.localeCompare(currencyB);
  });
};

const getTypeIcon = (type) => {
  switch (type) {
    case USER_TYPES.ADMIN:
      return BuildingOfficeIcon;
    case USER_TYPES.COUNTRY_ADMIN:
      return GlobeAsiaAustraliaIcon;
    case USER_TYPES.CITY_ADMIN:
      return BuildingStorefrontIcon;
    case USER_TYPES.SUPER_AGENT:
      return StarIcon;
    case USER_TYPES.AGENT:
      return UserGroupIcon;
    case USER_TYPES.PLAYER:
    default:
      return UserIcon;
  }
};

const getTypeColor = (type) => {
  switch (type) {
    case USER_TYPES.ADMIN:
      return 'text-purple-500';
    case USER_TYPES.COUNTRY_ADMIN:
      return 'text-blue-500';
    case USER_TYPES.CITY_ADMIN:
      return 'text-green-500';
    case USER_TYPES.SUPER_AGENT:
      return 'text-orange-500';
    case USER_TYPES.AGENT:
      return 'text-yellow-500';
    case USER_TYPES.PLAYER:
    default:
      return 'text-gray-500';
  }
};

const UserDeepView = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { showNotification } = useNotification();
  const { t } = useTranslation();
  const [userData, setUserData] = useState(null);
  const [userAttributes, setUserAttributes] = useState(null);
  const [originalAttributes, setOriginalAttributes] = useState(null);
  const [parentData, setParentData] = useState(null);
  const [directReports, setDirectReports] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [showBlockConfirm, setShowBlockConfirm] = useState(false);
  const [showCurrencyConfirm, setShowCurrencyConfirm] = useState(false);
  const [pendingCurrency, setPendingCurrency] = useState(null);
  const [isAttributesLoaded, setIsAttributesLoaded] = useState(false);
  const [isCurrentUser, setIsCurrentUser] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  // Country accounts see device type and IP on deep view, same as admins
  const [isCountry, setIsCountry] = useState(false);
  // Raw device fingerprint (technical + JSON) stays on the playwinadmin account
  const [isPlaywinAdmin, setIsPlaywinAdmin] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showIPDetails, setShowIPDetails] = useState(false);
  const [ipToInspect, setIpToInspect] = useState(null);
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [transactionType, setTransactionType] = useState('deposit');
  const [currentUserType, setCurrentUserType] = useState(null);
  const [canViewUser, setCanViewUser] = useState(true);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [expandedDirectReports, setExpandedDirectReports] = useState(false);
  const [networkBalances, setNetworkBalances] = useState({});
  const [networkBreakdown, setNetworkBreakdown] = useState({});
  const [includeOwnBalance, setIncludeOwnBalance] = useState(false);
  const [expandedCurrency, setExpandedCurrency] = useState(null);
  const [selectedTags, setSelectedTags] = useState([]);
  const [isRestricted, setIsRestricted] = useState(false);
  const [availableSites, setAvailableSites] = useState([]);
  const [selectedSites, setSelectedSites] = useState([]);
  const [isSitesLoading, setIsSitesLoading] = useState(false);

  // Admins and country accounts both see device type and last IP.
  const canSeeDeviceInfo = isAdmin || isCountry || isPlaywinAdmin;
  const deviceSummary = useMemo(
    () => deriveDeviceSummary(userAttributes),
    [userAttributes]
  );
  const deviceTypeLabel = useMemo(() => {
    if (!deviceSummary.hasAnyData) return null;
    const labels = {
      mobile: ['userDeepView.device.phone', 'Phone'],
      tablet: ['userDeepView.device.tablet', 'Tablet'],
      smarttv: ['userDeepView.device.smartTv', 'Smart TV'],
      desktop: ['userDeepView.device.computer', 'Computer']
    };
    const [key, fallback] = labels[deviceSummary.deviceType] || labels.desktop;
    return t(key, fallback);
  }, [deviceSummary, t]);
  const sessionIp = deviceSummary.ip || null;

  // User type hierarchy levels (higher number = higher authority)
  const userTypeHierarchy = {
    'player': 1,
    'agent': 2,
    'super-agent': 3,
    'city': 4,
    'country': 5,
    'admin': 6
  };

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Check if user is restricted
  useEffect(() => {
    const checkRestriction = async () => {
      const restricted = await isUserRestricted();
      setIsRestricted(restricted);
    };
    checkRestriction();
  }, []);

  useEffect(() => {
    const checkCurrentUser = async () => {
      try {
        const { tokens } = await fetchAuthSession();
        const username = tokens?.accessToken?.payload?.username;
        const userGroups = tokens?.accessToken?.payload['cognito:groups'] || [];
        const isCurrentUserViewing = username === userId;
        console.log('UserDeepView: Checking if current user', { username, userId, isCurrentUser: isCurrentUserViewing });
        setIsCurrentUser(isCurrentUserViewing);
        setIsAdmin(userGroups.includes('Admins') || userGroups.includes('playwinadmin'));
        setIsPlaywinAdmin((username || '').toLowerCase() === 'playwinadmin');

        // Countries group, with the user-tree type as a fallback when the token
        // has not picked up the group yet.
        let countryViewer = userGroups.includes('Countries') || userGroups.includes('Country');
        
        // Get current user's type from the cached data
        const { success, data } = await getUsersData();
        if (success && data.users[username]) {
          const currentUser = data.users[username];
          setCurrentUserType(currentUser.type);
          if (currentUser.type === USER_TYPES.COUNTRY_ADMIN) countryViewer = true;
        }
        setIsCountry(countryViewer);
      } catch (error) {
        console.error('Error checking current user:', error);
      }
    };
    checkCurrentUser();
  }, [userId]);

  // Optimized: Calculate network balance with breakdown by user
  const calculateNetworkBalanceWithBreakdown = (username, usersData, includeOwn = true) => {
    if (!username || !usersData) return { totals: {}, breakdown: {} };
    
    const user = usersData[username];
    if (!user) return { totals: {}, breakdown: {} };
    
    const totals = {};
    const topPerCurrency = {}; // { currency: [top 3 contributors] }
    
    const processBalances = (userData) => {
      if (!userData?.balances) return;
      
      Object.entries(userData.balances).forEach(([currency, balance]) => {
        const amount = typeof balance === 'object' ? (balance.amount || balance.value || 0) : balance;
        const numAmount = parseFloat(amount) || 0;
        if (numAmount <= 0) return;
        
        totals[currency] = (totals[currency] || 0) + numAmount;
        
        if (!topPerCurrency[currency]) {
          topPerCurrency[currency] = [];
        }
        
        topPerCurrency[currency].push({
          username: userData.username,
          name: userData.name || userData.username,
          type: userData.type,
          balance: numAmount
        });
        
        // Keep only top 3 (optimization: don't let array grow too large)
        if (topPerCurrency[currency].length > 10) {
          topPerCurrency[currency].sort((a, b) => b.balance - a.balance);
          topPerCurrency[currency] = topPerCurrency[currency].slice(0, 3);
        }
      });
    };
    
    if (includeOwn) {
      processBalances(user);
    }
    
    // Build parent->children index ONCE in O(N), then DFS in O(K)
    // This replaces the old O(K*N) approach that filtered all users per stack node
    const childrenByParent = {};
    for (const u of Object.values(usersData)) {
      if (u?.parentUsername) {
        if (!childrenByParent[u.parentUsername]) {
          childrenByParent[u.parentUsername] = [];
        }
        childrenByParent[u.parentUsername].push(u);
      }
    }
    
    // DFS using the pre-built index
    const stack = [username];
    const visited = new Set();
    
    while (stack.length > 0) {
      const currentUsername = stack.pop();
      if (visited.has(currentUsername)) continue;
      visited.add(currentUsername);
      
      const children = childrenByParent[currentUsername] || [];
      for (const child of children) {
        if (child?.username) {
          processBalances(child);
          stack.push(child.username);
        }
      }
    }
    
    // Final sort to get top 3 per currency
    const breakdown = {};
    Object.keys(topPerCurrency).forEach(currency => {
      breakdown[currency] = topPerCurrency[currency]
        .sort((a, b) => b.balance - a.balance)
        .slice(0, 3);
    });
    
    return { totals, breakdown };
  };

  useEffect(() => {
    const fetchData = async () => {
      let foundUser = false;
      try {
        // Get cached data first
        const { success, data } = await getUsersData();
        if (success) {
          // Find user in the cached flat data
          const user = data.users[userId];
          if (user) {
            foundUser = true;
            setUserData(user);
            // Find parent using parentUsername
            const parent = user.parentUsername ? data.users[user.parentUsername] : null;
            setParentData(parent || null);
            
            // Find direct reports (users who have this user as parent)
            const reports = Object.values(data.users)
              .filter(u => u.parentUsername === userId)
              .sort((a, b) => {
                // Sort by type hierarchy first, then by name
                const aLevel = userTypeHierarchy[a.type] || 0;
                const bLevel = userTypeHierarchy[b.type] || 0;
                if (aLevel !== bLevel) return bLevel - aLevel; // Higher levels first
                return (a.name || a.username).localeCompare(b.name || b.username);
              });
            setDirectReports(reports);
            
            // Check if current user can view this user
            if (currentUserType && !isCurrentUser) {
              const currentUserLevel = userTypeHierarchy[currentUserType] || 0;
              const targetUserLevel = userTypeHierarchy[user.type] || 0;
              
              // User cannot view someone at the same level or higher
              if (targetUserLevel >= currentUserLevel) {
                setCanViewUser(false);
              } else {
                setCanViewUser(true);
              }
            }
          }
        }

        // Always exit the loading state once the cache lookup is done.
        // If the user wasn't found, the "User not found" branch in render
        // takes over (see `if (!userData)` below the spinner). Without
        // this, a missing user (e.g. stale localStorage tree, hierarchy
        // change, partial Lambda response) leaves the page spinning forever.
        setIsLoading(false);

        // Fetch fresh attributes silently
        const attributes = await fetchUserAttributes(userId);
        setIsAttributesLoaded(true);
        if (JSON.stringify(attributes) !== JSON.stringify(userAttributes)) {
          setUserAttributes(attributes);
          setOriginalAttributes(JSON.parse(JSON.stringify(attributes)));
        }
      } catch (error) {
        console.error('Error:', error);
        setIsLoading(false);
        if (!foundUser) {
          showNotification(t('userDeepView.errors.loadingFailed', 'Error loading user data'), 'error');
        }
      }
    };

    fetchData();
  }, [userId, showNotification, t, currentUserType, isCurrentUser]);

  // Separate effect for network balance calculation (deferred, with cancellation)
  useEffect(() => {
    let cancelled = false;

    const calculateBalance = async () => {
      try {
        const { success, data } = await getUsersData();
        if (cancelled) return;
        if (success && data.users[userId] && data.users[userId].type !== 'player') {
          const { totals, breakdown } = calculateNetworkBalanceWithBreakdown(userId, data.users, includeOwnBalance);
          if (cancelled) return;
          setNetworkBalances(totals);
          setNetworkBreakdown(breakdown);
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Error calculating network balance:', error);
        }
      }
    };

    // Defer to let the page render first
    const timerId = setTimeout(calculateBalance, 0);

    return () => {
      cancelled = true;
      clearTimeout(timerId);
    };
  }, [userId, includeOwnBalance]);

  // Fetch user attributes separately
  useEffect(() => {
    const fetchAttributes = async () => {
      try {
        const attributes = await fetchUserAttributes(userId);
        setIsAttributesLoaded(true);
        if (JSON.stringify(attributes) !== JSON.stringify(userAttributes)) {
          setUserAttributes(attributes);
          setOriginalAttributes(JSON.parse(JSON.stringify(attributes)));
          const REMOVED_TAGS = ['Rebeltech', 'Rebeltech Live', 'Rebeltech Slots'];
          let tags = [];
          if (attributes?.tags && Array.isArray(attributes.tags)) {
            tags = attributes.tags.filter(t => !REMOVED_TAGS.includes(t));
          } else if (attributes?.tag && !REMOVED_TAGS.includes(attributes.tag)) {
            tags = [attributes.tag];
          }
          
          setSelectedTags(tags);
          if (JSON.stringify(tags) !== JSON.stringify(attributes?.tags)) {
            setUserAttributes(prev => ({ ...prev, tags }));
          }

          if (attributes?.allowedSites && Array.isArray(attributes.allowedSites)) {
            setSelectedSites(attributes.allowedSites);
          } else {
            setSelectedSites([]);
          }
        }
      } catch (error) {
        console.error('Error fetching attributes:', error);
      }
    };
    
    if (userId) {
      fetchAttributes();
    }
  }, [userId]);

  // Fetch available sites from Amplify -- only when admin is viewing a country user
  useEffect(() => {
    if (!isAdmin || !userData || userData.type !== USER_TYPES.COUNTRY_ADMIN || isCurrentUser) return;

    const loadSites = async () => {
      setIsSitesLoading(true);
      try {
        const sites = await fetchAvailableSites();
        setAvailableSites(sites);
      } catch (error) {
        console.error('Error loading available sites:', error);
      } finally {
        setIsSitesLoading(false);
      }
    };

    loadSites();
  }, [isAdmin, userData, isCurrentUser]);

  useEffect(() => {
    // Detect iOS device
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    setIsIOS(isIOSDevice);
  }, []);

  const hasChanges = () => {
    if (!originalAttributes || !userAttributes) return false;
    return JSON.stringify(originalAttributes) !== JSON.stringify(userAttributes);
  };

  const handleToggleBlocked = () => {
    if (isCurrentUser) return; // Prevent self-blocking
    if (!userAttributes?.blocked) {
      setShowBlockConfirm(true);
    } else {
      setUserAttributes(prev => ({
        ...prev,
        blocked: false
      }));
    }
  };

  const confirmBlock = () => {
    setUserAttributes(prev => ({
      ...prev,
      blocked: true
    }));
    setShowBlockConfirm(false);
  };

  const handleCurrencyToggle = (currency) => {
    if (isCurrentUser) return; // Prevent self-currency modification
    
    // Check if currency is already selected
    const currencies = userAttributes?.currencies || [];
    if (currencies.includes(currency)) {
      // Don't allow deselecting - show notification
      showNotification(t('userDeepView.errors.cannotRemoveCurrency'), 'warning');
      return;
    }

    // Player-specific rule: at most one of USD/TND/TRY/IQD/KWD (LBP may be added separately).
    // Since removing currencies is not supported here, we block adding a second
    // exclusive currency rather than silently replacing the existing one.
    if (
      userData?.type === USER_TYPES.PLAYER &&
      EXCLUSIVE_PLAYER_CURRENCIES.includes(currency) &&
      currencies.some(c => EXCLUSIVE_PLAYER_CURRENCIES.includes(c))
    ) {
      showNotification(
        t(
          'userDeepView.errors.playerCurrencyExclusive',
          'Players can only have one of USD, TND, TRY, IQD, or KWD. LBP can be added as a second currency.'
        ),
        'warning'
      );
      return;
    }

    // Show confirmation modal for adding currency
    setPendingCurrency(currency);
    setShowCurrencyConfirm(true);
  };

  const confirmAddCurrency = () => {
    if (pendingCurrency) {
      setUserAttributes(prev => ({
        ...prev,
        currencies: [...(prev.currencies || []), pendingCurrency]
      }));
      setPendingCurrency(null);
      setShowCurrencyConfirm(false);
      // Don't show notification here - it will be shown after successful save and wallet initialization
    }
  };

  const handleSave = async () => {
    if (!isAttributesLoaded) {
      showNotification(t('userDeepView.errors.waitForAttributes', 'Please wait for the latest attributes to load'), 'warning');
      return;
    }

    try {
      setIsSaving(true);
      
      // Get auth token
      const { tokens } = await fetchAuthSession();
      if (!tokens?.idToken) {
        throw new Error('Please log in again');
      }
      
      // First, update user attributes
      await updateUserAttributes(userId, userAttributes);
      
      // Check if there are new currencies that need wallet initialization
      const newCurrencies = userAttributes?.currencies?.filter(
        (currency) => !originalAttributes?.currencies?.includes(currency)
      );

      if (newCurrencies && newCurrencies.length > 0) {
        try {
          // Call the coreagent API to initialize wallet for new currencies
          await apiPost(
            'coreAgent',
            '/core/agent',
            {
              action: 'initializeWallet',
              username: userId,
              currencies: newCurrencies
            },
            tokens.idToken.toString()
          );
          // Only show wallet initialized message if the API call succeeds
          const currencyList = newCurrencies.join(', ');
          showNotification(
            t('userDeepView.success.walletInitialized', `Wallet initialized for new currencies: ${currencyList}`),
            'success'
          );
        } catch (walletError) {
          console.error('Error initializing wallet:', walletError);
          // Don't fail the whole operation if wallet initialization fails
          const errorMessage = walletError.message || 'Unknown error';
          showNotification(
            t('userDeepView.warning.walletInitializationFailed', 
              `User updated but wallet initialization failed: ${errorMessage}`),
            'warning'
          );
        }
      }
      
      // Update the original attributes to reflect the saved state
      setOriginalAttributes(JSON.parse(JSON.stringify(userAttributes)));
      showNotification(t('userDeepView.success.attributesUpdated', 'User attributes updated successfully'), 'success');
      
    } catch (error) {
      console.error('Error updating user attributes:', error);
      showNotification(t('userDeepView.errors.updateFailed', 'Error updating user attributes'), 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleNavigateToReports = () => {
    // Navigate to Reports page with the selected user
    navigate('/reports', { 
      state: { 
        selectedUser: {
          ...userData,
          attributes: userAttributes || {},
          balances: userData?.balances || {}
        } 
      }
    });
  };

  const handleNavigateToTransactionHistory = () => {
    // Navigate to Transactions page with this user selected
    navigate('/transactions', { 
      state: { 
        selectedUser: {
          username: userData?.username,
          name: userData?.name,
          type: userData?.type,
          attributes: userAttributes || {},
          balances: userData?.balances || {}
        }
      }
    });
  };

  const handleNavigateToBetHistory = () => {
    // Navigate to BetHistory page with this user selected
    navigate('/bet-history', { 
      state: { 
        selectedUser: {
          username: userData?.username,
          name: userData?.name,
          type: userData?.type,
          attributes: userAttributes || {},
          balances: userData?.balances || {}
        }
      }
    });
  };

  const handleOpenTransaction = (type) => {
    setTransactionType(type);
    setShowTransactionModal(true);
  };

  const handleTransactionSuccess = () => {
    // Refresh user data after successful transaction
    setShowTransactionModal(false);
    showNotification(t('userDeepView.success.transactionCompleted', 'Transaction completed successfully'), 'success');
    // Optionally refresh the user attributes
    fetchUserAttributes(userId).then(attributes => {
      if (attributes) {
        setUserAttributes(attributes);
        setOriginalAttributes(JSON.parse(JSON.stringify(attributes)));
      }
    });
  };

  // Casino provider tag values - read by the casino package via attributes.tags
  // - 'Betinovio'       -> live + slots (legacy value, still written when both are on)
  // - 'Betinovio Live'  -> live casino only
  // - 'Betinovio Slots' -> slots / regular casino only
  // The same three values exist for 'Databetrix'.
  const CASINO_PROVIDERS = [
    {
      base: 'Betinovio',
      cardOn: 'border-sky-500 bg-sky-50 text-sky-700 dark:border-sky-400 dark:bg-sky-900/50 dark:text-sky-300 shadow-md',
      cardOff: 'border-gray-300 bg-white text-gray-700 hover:border-sky-400 hover:bg-sky-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300 dark:hover:border-sky-500 dark:hover:bg-sky-900/30',
      segmentOn: 'bg-sky-500 text-white dark:bg-sky-600',
      segmentOff: 'bg-white text-gray-700 hover:bg-sky-50 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-sky-900/30'
    },
    {
      base: 'Databetrix',
      cardOn: 'border-rose-500 bg-rose-50 text-rose-700 dark:border-rose-400 dark:bg-rose-900/50 dark:text-rose-300 shadow-md',
      cardOff: 'border-gray-300 bg-white text-gray-700 hover:border-rose-400 hover:bg-rose-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300 dark:hover:border-rose-500 dark:hover:bg-rose-900/30',
      segmentOn: 'bg-rose-500 text-white dark:bg-rose-600',
      segmentOff: 'bg-white text-gray-700 hover:bg-rose-50 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-rose-900/30'
    }
  ];

  const casinoTags = (base) => ({ both: base, live: `${base} Live`, slots: `${base} Slots` });

  // Derive which products are enabled for a casino provider from the current tags.
  // Returns 'live' | 'slots' | 'both' | null, where null means the provider is off.
  const getCasinoMode = (base) => {
    const { both, live, slots } = casinoTags(base);
    const hasLive = selectedTags.includes(live);
    const hasSlots = selectedTags.includes(slots);
    if (selectedTags.includes(both) || (hasLive && hasSlots)) return 'both';
    if (hasLive) return 'live';
    if (hasSlots) return 'slots';
    return null;
  };

  const handleCasinoModeChange = (base, mode) => {
    const { both, live, slots } = casinoTags(base);

    setSelectedTags(prev => {
      // Strip this provider's tags first, keep everything else (other provider, sports, ...)
      const otherTags = prev.filter(tag => tag !== both && tag !== live && tag !== slots);

      let nextProviderTags = [];
      if (mode === 'both') nextProviderTags = [both];
      else if (mode === 'live') nextProviderTags = [live];
      else if (mode === 'slots') nextProviderTags = [slots];

      const newTags = [...otherTags, ...nextProviderTags];

      setUserAttributes(current => ({
        ...current,
        tags: newTags
      }));

      return newTags;
    });
  };

  // Sports provider tag values - read by the casino package via attributes.tags
  // - 'GR8 Sports'          -> GR8 Sports only
  // - 'BetConstruct Sports' -> BetConstruct Sports only
  // - both present          -> Both providers enabled
  const SPORTS_TAGS = {
    GR8: 'GR8 Sports',
    BETCONSTRUCT: 'BetConstruct Sports'
  };

  // Derive which segment is active from the current tag selection.
  // Returns 'gr8' | 'betconstruct' | 'both' | null
  const sportsProvider = (() => {
    const hasGr8 = selectedTags.includes(SPORTS_TAGS.GR8);
    const hasBc = selectedTags.includes(SPORTS_TAGS.BETCONSTRUCT);
    if (hasGr8 && hasBc) return 'both';
    if (hasGr8) return 'gr8';
    if (hasBc) return 'betconstruct';
    return null;
  })();

  const handleSportsProviderChange = (provider) => {
    setSelectedTags(prev => {
      // Strip any existing sports tags first, keep all other (e.g. Betinovio) tags
      const otherTags = prev.filter(
        tag => tag !== SPORTS_TAGS.GR8 && tag !== SPORTS_TAGS.BETCONSTRUCT
      );

      let nextSportsTags = [];
      if (provider === 'gr8') nextSportsTags = [SPORTS_TAGS.GR8];
      else if (provider === 'betconstruct') nextSportsTags = [SPORTS_TAGS.BETCONSTRUCT];
      else if (provider === 'both') nextSportsTags = [SPORTS_TAGS.GR8, SPORTS_TAGS.BETCONSTRUCT];

      const newTags = [...otherTags, ...nextSportsTags];

      setUserAttributes(current => ({
        ...current,
        tags: newTags
      }));

      return newTags;
    });
  };

  const handleSiteToggle = (site) => {
    setSelectedSites(prev => {
      const newSites = prev.includes(site)
        ? prev.filter(s => s !== site)
        : [...prev, site];

      setUserAttributes(current => ({
        ...current,
        allowedSites: newSites
      }));

      return newSites;
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen p-4">
        <div className="flex items-center space-x-3">
          <svg className="animate-spin h-8 w-8 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <span className="text-lg text-gray-700 dark:text-gray-200">{t('userDeepView.loading', 'Loading user data...')}</span>
        </div>
      </div>
    );
  }

  if (!userData) {
    return (
      <div className="p-4 text-center">
        <XCircleIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">{t('userDeepView.userNotFound', 'User not found')}</h2>
      </div>
    );
  }

  // Check if user has permission to view this user
  if (!canViewUser && !isCurrentUser) {
    return (
      <div className="p-4 max-w-2xl mx-auto">
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-8 text-center">
          <ExclamationTriangleIcon className="h-12 w-12 text-yellow-600 dark:text-yellow-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            {t('userDeepView.accessDenied', 'Access Denied')}
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            {t('userDeepView.cannotViewSameOrHigherLevel', 'You cannot view users at the same level or higher in the hierarchy.')}
          </p>
          <button
            onClick={handleBack}
            className="inline-flex items-center px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors"
          >
            <ArrowLeftIcon className="w-4 h-4 mr-2" />
            {t('common.back', 'Go Back')}
          </button>
        </div>
      </div>
    );
  }

  const formatDate = (dateString) => {
    if (!dateString) return t('common.na', 'N/A');
    return new Date(dateString).toLocaleString();
  };

  return (
    <div className={`max-w-4xl mx-auto ${isIOS ? 'pb-safe' : 'pb-4'}`}>
      {/* Sticky Header */}
      <div className={`sticky top-0 z-20 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 
        ${isIOS ? 'pt-safe' : ''}`}>
        <div className="px-4 py-3">
          <div className="flex items-center gap-3">
            <button
              onClick={handleBack}
              className={`inline-flex items-center justify-center p-2 rounded-full
                bg-white dark:bg-gray-800 shadow-sm hover:shadow-md
                hover:bg-gray-100 dark:hover:bg-gray-700 transition-all
                ${isIOS ? 'active:scale-95' : ''}`}
              aria-label={t('common.back', 'Back')}
            >
              <ArrowLeftIcon className="w-5 h-5" />
            </button>
            <div className="flex-1 min-w-0">
              <h1 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2 truncate">
                {React.createElement(getTypeIcon(userData?.type), {
                  className: `w-5 h-5 sm:w-6 sm:h-6 flex-shrink-0 ${getTypeColor(userData?.type)}`
                })}
                <span className="truncate">{userData?.name || userData?.username}</span>
              </h1>
              <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                @{userData?.username} • {t(`userTypes.${userData?.type}`, userData?.type)}
              </p>
            </div>
            {!isAttributesLoaded && !isCurrentUser && (
              <div className="w-4 h-4 relative flex-shrink-0">
                <div className="absolute inset-0 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
              </div>
            )}
          </div>
          {hasChanges() && !isCurrentUser && (
            <div className="mt-2 px-2 py-1 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
              <span className="text-xs font-medium text-amber-700 dark:text-amber-300 flex items-center gap-1">
                <ClockIcon className="w-3 h-3" />
                {t('userDeepView.unsavedChanges', 'Unsaved changes')}
              </span>
            </div>
          )}
        </div>
      </div>
      
      <div className="p-4 space-y-4">
      
      {/* Basic Info */}
      <section className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm 
        ${isIOS ? 'rounded-2xl' : ''}`}>
        <div className="p-4">
          <h2 className="text-lg font-semibold mb-3 text-gray-900 dark:text-white flex items-center gap-2">
            <ShieldCheckIcon className="w-5 h-5 text-emerald-500" />
            {t('userDeepView.basicInfo', 'Basic Information')}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-700">
              <span className="text-sm text-gray-600 dark:text-gray-400">{t('auth.username', 'Username')}</span>
              <span className="text-sm font-medium text-gray-900 dark:text-white font-mono">@{userData?.username}</span>
            </div>
            <div className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-700">
              <span className="text-sm text-gray-600 dark:text-gray-400">{t('users.name', 'Name')}</span>
              <span className="text-sm font-medium text-gray-900 dark:text-white">{userData?.name || t('common.na', 'N/A')}</span>
            </div>
            <div className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-700">
              <span className="text-sm text-gray-600 dark:text-gray-400">{t('users.role', 'Type')}</span>
              <span className="text-sm font-medium text-gray-900 dark:text-white flex items-center gap-1">
                {React.createElement(getTypeIcon(userData?.type), {
                  className: `w-4 h-4 ${getTypeColor(userData?.type)}`
                })}
                {t(`userTypes.${userData?.type}`, userData?.type)}
              </span>
            </div>
            {!isCurrentUser && (
              <div className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-700">
                <span className="text-sm text-gray-600 dark:text-gray-400">{t('agentTree.parent', 'Parent')}</span>
                {userData?.parentUsername ? (
                  <button
                    onClick={() => navigate(`/users/${userData.parentUsername}/deep-view`)}
                    className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1"
                  >
                    {React.createElement(getTypeIcon(parentData?.type), {
                      className: `w-3 h-3 ${getTypeColor(parentData?.type)}`
                    })}
                    @{userData.parentUsername}
                  </button>
                ) : (
                  <span className="text-sm font-medium text-gray-500">{t('common.na', 'N/A')}</span>
                )}
              </div>
            )}
            <div className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-700">
              <span className="text-sm text-gray-600 dark:text-gray-400">{t('userDeepView.creationDate', 'Created')}</span>
              <span className="text-sm font-medium text-gray-900 dark:text-white">
                {userAttributes?.creationDate ? new Date(userAttributes.creationDate).toLocaleDateString() : t('common.na', 'N/A')}
              </span>
            </div>
            <div className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-700">
              <span className="text-sm text-gray-600 dark:text-gray-400">{t('userDeepView.lastLogin', 'Last Login')}</span>
              <span className="text-sm font-medium text-gray-900 dark:text-white">
                {userAttributes?.lastLoginTime ? new Date(userAttributes.lastLoginTime).toLocaleDateString() : t('common.na', 'N/A')}
              </span>
            </div>
            {canSeeDeviceInfo && (
              <div className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-700">
                <span className="text-sm text-gray-600 dark:text-gray-400">{t('userDeepView.deviceType', 'Device type')}</span>
                <span className="text-sm font-medium text-gray-900 dark:text-white text-right">
                  {deviceTypeLabel
                    ? [deviceTypeLabel, deviceSummary.model].filter(Boolean).join(' · ')
                    : t('common.na', 'N/A')}
                </span>
              </div>
            )}
            {canSeeDeviceInfo && (
              <div className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-700">
                <span className="text-sm text-gray-600 dark:text-gray-400">{t('userDeepView.lastIPAddress', 'IP Address')}</span>
                {sessionIp ? (
                  <button
                    onClick={() => {
                      setIpToInspect(sessionIp);
                      setShowIPDetails(true);
                    }}
                    className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1"
                  >
                    {sessionIp}
                    <ArrowTopRightOnSquareIcon className="w-3 h-3" />
                  </button>
                ) : (
                  <span className="text-sm font-medium text-gray-500">
                    {t('common.na', 'N/A')}
                  </span>
                )}
              </div>
            )}
            <div className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-700">
              <span className="text-sm text-gray-600 dark:text-gray-400">{t('users.status', 'Status')}</span>
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                userAttributes?.blocked 
                  ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' 
                  : 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
              }`}>
                {userAttributes?.blocked ? t('userDeepView.blocked', 'Blocked') : t('userDeepView.active', 'Active')}
              </span>
            </div>
            {!isCurrentUser && (
              <div className="sm:col-span-2 flex justify-end mt-2">
                <button
                  onClick={handleToggleBlocked}
                  className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium transition-colors
                    ${userAttributes?.blocked 
                      ? 'bg-red-100 text-red-800 hover:bg-red-200 dark:bg-red-900/50 dark:text-red-200'
                      : 'bg-green-100 text-green-800 hover:bg-green-200 dark:bg-green-900/50 dark:text-green-200'
                    }`}
                >
                  <LockClosedIcon className="w-3 h-3 mr-1" />
                  {userAttributes?.blocked ? t('userDeepView.unblock', 'Unblock User') : t('userDeepView.block', 'Block User')}
                </button>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Device & login card: admins and country accounts. Raw fingerprint stays on playwinadmin. */}
      {canSeeDeviceInfo && (
        <DeviceInfoPanel
          attributes={userAttributes}
          isIOS={isIOS}
          allowTechnical={isPlaywinAdmin}
          onViewIP={(ip) => {
            setIpToInspect(ip);
            setShowIPDetails(true);
          }}
        />
      )}

      {/* Tags Section - Only for Admins/playwinadmin viewing country users */}
      {isAdmin && !isCurrentUser && userData?.type === USER_TYPES.COUNTRY_ADMIN && (
        <section className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm 
          ${isIOS ? 'rounded-2xl' : ''}`}>
          <div className="p-4">
            <h2 className="text-lg font-semibold mb-3 text-gray-900 dark:text-white flex items-center gap-2">
              <StarIcon className="w-5 h-5 text-amber-500" />
              {t('userDeepView.tags', 'Tags')}
            </h2>
            <div className="space-y-3">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {t('userDeepView.tagsDescription', 'Select organization tags for this country (multiple selections allowed)')}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {CASINO_PROVIDERS.map((provider) => {
                  const mode = getCasinoMode(provider.base);
                  const isEnabled = mode !== null;
                  return (
                    <div
                      key={provider.base}
                      className={`rounded-lg border-2 transition-all
                        ${isEnabled ? provider.cardOn : provider.cardOff}`}
                    >
                      <button
                        onClick={() => handleCasinoModeChange(provider.base, isEnabled ? null : 'both')}
                        aria-pressed={isEnabled}
                        className={`w-full min-h-[48px] flex items-center justify-between gap-2 px-4 py-3 font-medium
                          ${isIOS ? 'active:scale-95' : ''}`}
                      >
                        <span className="text-base">{provider.base}</span>
                        {isEnabled && <CheckCircleIcon className="w-5 h-5 flex-shrink-0" />}
                      </button>
                      {isEnabled && (
                        <div className="px-3 pb-3">
                          <div className="grid grid-cols-3 rounded-lg border-2 border-gray-300 dark:border-gray-600 overflow-hidden">
                            {[
                              { key: 'live', label: t('userDeepView.casinoLive', 'Live') },
                              { key: 'slots', label: t('userDeepView.casinoSlots', 'Slots') },
                              { key: 'both', label: t('userDeepView.casinoBoth', 'Both') }
                            ].map((option, idx) => (
                              <button
                                key={option.key}
                                onClick={() => handleCasinoModeChange(provider.base, option.key)}
                                aria-pressed={mode === option.key}
                                className={`min-h-[44px] px-1 py-2 text-xs sm:text-sm font-medium leading-tight transition-all
                                  ${idx > 0 ? 'border-l-2 border-gray-300 dark:border-gray-600' : ''}
                                  ${mode === option.key ? provider.segmentOn : provider.segmentOff}
                                  ${isIOS ? 'active:scale-95' : ''}`}
                              >
                                {option.label}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              {/* Sports Provider 3-way Toggle */}
              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                  <GlobeAltIcon className="w-4 h-4 text-indigo-500" />
                  {t('userDeepView.sportsProvider', 'Sports Provider')}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                  {t(
                    'userDeepView.sportsProviderDescription',
                    'Choose which sportsbook provider is enabled for this country.'
                  )}
                </p>
                <div className="grid grid-cols-3 w-full sm:inline-grid sm:w-auto rounded-lg border-2 border-gray-300 dark:border-gray-600 overflow-hidden bg-white dark:bg-gray-700">
                  {[
                    { key: 'gr8', label: 'GR8 Sports' },
                    { key: 'betconstruct', label: 'BetConstruct Sports' },
                    { key: 'both', label: t('userDeepView.sportsBoth', 'Both') }
                  ].map((option, idx) => {
                    const isActive = sportsProvider === option.key;
                    return (
                      <button
                        key={option.key}
                        onClick={() => handleSportsProviderChange(option.key)}
                        className={`
                          min-h-[44px] px-2 sm:px-4 py-2 text-xs sm:text-sm font-medium leading-tight transition-all
                          ${idx > 0 ? 'border-l-2 border-gray-300 dark:border-gray-600' : ''}
                          ${isActive
                            ? 'bg-indigo-500 text-white dark:bg-indigo-600'
                            : 'bg-white text-gray-700 hover:bg-indigo-50 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-indigo-900/30'}
                          ${isIOS ? 'active:scale-95' : ''}
                        `}
                      >
                        {option.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {selectedTags.length > 0 && (
                <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                  <p className="text-sm text-blue-700 dark:text-blue-300 flex flex-wrap items-center gap-x-2 gap-y-1">
                    <CheckCircleIcon className="w-4 h-4 flex-shrink-0" />
                    {t('userDeepView.currentTags', 'Current tags')}: 
                    <span className="font-semibold flex flex-wrap gap-x-2 gap-y-1">
                      {selectedTags.map((tag, index) => (
                        <span key={tag}>
                          {tag}{index < selectedTags.length - 1 ? ',' : ''}
                        </span>
                      ))}
                    </span>
                  </p>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Allowed Sites Section - Only for Admins/playwinadmin viewing country users */}
      {isAdmin && !isCurrentUser && userData?.type === USER_TYPES.COUNTRY_ADMIN && (
        <section className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm 
          ${isIOS ? 'rounded-2xl' : ''}`}>
          <div className="p-4">
            <h2 className="text-lg font-semibold mb-3 text-gray-900 dark:text-white flex items-center gap-2">
              <GlobeAltIcon className="w-5 h-5 text-blue-500" />
              {t('userDeepView.allowedSites', 'Allowed Sites')}
            </h2>
            <div className="space-y-3">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {t('userDeepView.allowedSitesDescription', 'Select which sites players under this country can access. No selection means unrestricted access.')}
              </p>
              {isSitesLoading ? (
                <div className="flex items-center gap-2 py-4 justify-center">
                  <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {t('userDeepView.loadingSites', 'Loading available sites...')}
                  </span>
                </div>
              ) : availableSites.length > 0 ? (
                <div className="flex flex-wrap gap-3">
                  {availableSites.map((site) => (
                    <button
                      key={site}
                      onClick={() => handleSiteToggle(site)}
                      className={`
                        inline-flex items-center px-5 py-3 rounded-lg border-2 transition-all font-medium
                        ${selectedSites.includes(site)
                          ? 'border-blue-500 bg-blue-50 text-blue-700 dark:border-blue-400 dark:bg-blue-900/50 dark:text-blue-300 shadow-md'
                          : 'border-gray-300 bg-white text-gray-700 hover:border-blue-400 hover:bg-blue-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300 dark:hover:border-blue-500 dark:hover:bg-blue-900/30'}
                        ${isIOS ? 'active:scale-95' : ''}
                      `}
                    >
                      <GlobeAltIcon className="w-4 h-4 mr-2 flex-shrink-0" />
                      <span className="text-sm">{site}</span>
                      {selectedSites.includes(site) && (
                        <CheckCircleIcon className="w-5 h-5 ml-2" />
                      )}
                    </button>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4 bg-gray-50 dark:bg-gray-900/50 rounded-lg border border-gray-200 dark:border-gray-700">
                  <GlobeAltIcon className="w-6 h-6 text-gray-400 dark:text-gray-600 mx-auto mb-2" />
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {t('userDeepView.noSitesAvailable', 'No sites found')}
                  </p>
                </div>
              )}
              {selectedSites.length > 0 && (
                <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                  <p className="text-sm text-blue-700 dark:text-blue-300 flex items-center gap-2">
                    <CheckCircleIcon className="w-4 h-4" />
                    {t('userDeepView.currentSites', 'Allowed sites')}:
                    <span className="font-semibold flex flex-wrap gap-2 ml-1">
                      {selectedSites.map((site, index) => (
                        <span key={site}>
                          {site}{index < selectedSites.length - 1 ? ',' : ''}
                        </span>
                      ))}
                    </span>
                  </p>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Direct Reports Section */}
      {directReports.length > 0 && (
        <section className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm 
          ${isIOS ? 'rounded-2xl' : ''}`}>
          <div className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <UserGroupIcon className="w-5 h-5 text-blue-500" />
                {t('userDeepView.directReports', 'Direct Reports')} ({directReports.length})
              </h2>
              <button
                onClick={() => setExpandedDirectReports(!expandedDirectReports)}
                className={`flex items-center gap-1 text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors
                  ${isIOS ? 'active:scale-95' : ''}`}
              >
                <span>{expandedDirectReports ? t('common.showLess', 'Hide') : t('common.showAll', 'View')}</span>
                {expandedDirectReports ? (
                  <ChevronUpIcon className="w-4 h-4" />
                ) : (
                  <ChevronDownIcon className="w-4 h-4" />
                )}
              </button>
            </div>
            {expandedDirectReports && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {directReports.map((report) => (
                <button
                  key={report.username}
                  onClick={() => navigate(`/users/${report.username}/deep-view`)}
                  className={`flex items-center gap-3 p-3 rounded-lg border transition-all
                    border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50
                    hover:bg-gray-100 dark:hover:bg-gray-800 hover:border-blue-300 dark:hover:border-blue-600
                    ${isIOS ? 'active:scale-95' : ''}
                  `}
                >
                  {React.createElement(getTypeIcon(report.type), {
                    className: `w-5 h-5 flex-shrink-0 ${getTypeColor(report.type)}`
                  })}
                  <div className="flex flex-col items-start text-left min-w-0">
                    <span className="text-sm font-medium text-gray-900 dark:text-white truncate w-full">
                      {report.name || report.username}
                    </span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      @{report.username} • {t(`userTypes.${report.type}`, report.type)}
                    </span>
                    {report.balances && Object.keys(report.balances).length > 0 && (
                      <span className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {Object.entries(report.balances).slice(0, 2).map(([currency, balance]) => {
                          const amount = typeof balance === 'object' ? (balance.amount || balance.value || 0) : balance;
                          return `${currency}: ${parseFloat(amount).toFixed(2)}`;
                        }).join(', ')}
                        {Object.keys(report.balances).length > 2 && '...'}
                      </span>
                    )}
                  </div>
                </button>
                ))}
              </div>
            )}
            {!expandedDirectReports && (
              <div className="text-center py-4">
                <button
                  onClick={() => setExpandedDirectReports(true)}
                  className={`text-sm text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors
                    ${isIOS ? 'active:scale-95' : ''}`}
                >
                  {directReports.length} {t('userDeepView.directReportsCollapsed', directReports.length === 1 ? 'direct report' : 'direct reports')}
                </button>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Wallet Section */}
      <section className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm 
        ${isIOS ? 'rounded-2xl' : ''}`}>
        <div className="p-4">
          <h2 className="text-lg font-semibold mb-3 text-gray-900 dark:text-white flex items-center gap-2">
            <WalletIcon className="w-5 h-5 text-indigo-500" />
            {t('userDeepView.walletBalances', 'Wallet Balances')}
          </h2>
          
          {/* Personal Balance */}
          <div className="mb-4 bg-green-50 dark:bg-green-900/10 rounded-lg p-3 border border-green-200 dark:border-green-800">
            <h3 className="text-sm font-semibold text-green-800 dark:text-green-300 mb-3 flex items-center gap-2">
              <UserIcon className="w-4 h-4" />
              {t('userDeepView.personalBalance', 'Personal Balance')}
            </h3>
            {userData?.balances && Object.keys(userData.balances).length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
              {sortCurrencies(Object.entries(userData.balances)).map(([currency, balance]) => {
                const amount = typeof balance === 'object' ? (balance.amount || balance.value || 0) : balance;
                const numericAmount = parseFloat(amount) || 0;
                const digits = amountFractionDigits(currency, numericAmount);
                
                return (
                  <div
                    key={currency}
                    className={`relative rounded-lg border p-3 transition-all hover:shadow-md bg-white dark:bg-gray-800 border-green-300 dark:border-green-700
                      ${isIOS ? 'active:scale-95' : ''}
                    `}
                  >
                    <div className="flex flex-col">
                      <span className="text-xs text-green-700 dark:text-green-400 font-semibold">
                        {currency}
                      </span>
                      <span className="text-sm font-bold mt-1 text-green-800 dark:text-green-300">
                        {new Intl.NumberFormat('en-US', {
                          minimumFractionDigits: digits,
                          maximumFractionDigits: digits,
                        }).format(numericAmount)}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
            ) : (
              <div className="text-center py-4 bg-white dark:bg-gray-800 rounded-lg border border-green-200 dark:border-green-700">
                <WalletIcon className="w-6 h-6 text-green-400 dark:text-green-600 mx-auto mb-2" />
                <p className="text-sm text-green-600 dark:text-green-400">
                  {t('userDeepView.noWalletData', 'No wallet data available')}
                </p>
              </div>
            )}
          </div>

          {/* Network Balance - Hide for Players */}
          {userData?.type !== 'player' && networkBreakdown && Object.keys(networkBreakdown).length > 0 && (
            <div className="bg-blue-50 dark:bg-blue-900/10 rounded-lg p-3 border border-blue-200 dark:border-blue-800">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold text-blue-800 dark:text-blue-300 flex items-center gap-2">
                  <UserGroupIcon className="w-4 h-4" />
                  {t('userDeepView.networkBalance', 'Network Balance')}
                </h3>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={includeOwnBalance}
                    onChange={(e) => setIncludeOwnBalance(e.target.checked)}
                    className="w-4 h-4 text-blue-600 bg-white border-blue-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2"
                  />
                  <span className="text-xs text-blue-700 dark:text-blue-400 font-medium">
                    {t('userDeepView.includeOwn', 'Include own')}
                  </span>
                </label>
              </div>
              <p className="text-xs text-blue-600 dark:text-blue-400 mb-3 ml-6">
                {t('userDeepView.includesDownline', 'Includes all subordinates')}
              </p>
              {(() => {
                const filteredEntries = sortCurrencies(Object.entries(networkBalances))
                  .filter(([currency, balance]) => {
                    const amount = typeof balance === 'object' ? (balance.amount || balance.value || 0) : balance;
                    const numericAmount = parseFloat(amount) || 0;
                    return numericAmount > 0; // Only show currencies with positive balance
                  });
                
                if (filteredEntries.length === 0) {
                  return (
                    <p className="text-sm text-blue-600 dark:text-blue-400 text-center py-3">
                      {t('userDeepView.noNetworkBalance', 'No network balance')}
                    </p>
                  );
                }
                
                return (
                  <div className="space-y-2">
                    {filteredEntries.map(([currency, balance]) => {
                      const amount = typeof balance === 'object' ? (balance.amount || balance.value || 0) : balance;
                      const numericAmount = parseFloat(amount) || 0;
                      const digits = amountFractionDigits(currency, numericAmount);
                      const topContributors = networkBreakdown[currency] || [];
                      const isExpanded = expandedCurrency === currency;
                      
                      return (
                        <div key={currency} className="space-y-2">
                          <button
                            onClick={() => setExpandedCurrency(isExpanded ? null : currency)}
                            className={`w-full rounded-lg border p-3 transition-all bg-white dark:bg-gray-800 border-blue-300 dark:border-blue-700 hover:border-blue-400 dark:hover:border-blue-600
                              ${isIOS ? 'active:scale-95' : ''}
                            `}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex flex-col items-start">
                                <span className="text-xs text-blue-700 dark:text-blue-400 font-semibold">
                                  {currency}
                                </span>
                                <span className="text-lg font-bold text-blue-800 dark:text-blue-300">
                                  {new Intl.NumberFormat('en-US', {
                                    minimumFractionDigits: digits,
                                    maximumFractionDigits: digits,
                                  }).format(numericAmount)}
                                </span>
                              </div>
                              {topContributors.length > 0 && (
                                <div className="flex items-center gap-1">
                                  <span className="text-xs text-blue-600 dark:text-blue-400">
                                    {t('userDeepView.topContributors', 'Top 3')}
                                  </span>
                                  <ChevronDownIcon className={`w-4 h-4 text-blue-600 dark:text-blue-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                                </div>
                              )}
                            </div>
                          </button>
                          
                          {isExpanded && topContributors.length > 0 && (
                            <div className="pl-3 space-y-1">
                              {topContributors.map((contributor, idx) => {
                                const ContribIcon = getTypeIcon(contributor.type);
                                const contribDigits = amountFractionDigits(currency, contributor.balance);
                                return (
                                  <div
                                    key={contributor.username}
                                    className="flex items-center justify-between py-2 px-3 rounded-lg bg-blue-50/50 dark:bg-blue-900/5 border-l-2 border-blue-400 dark:border-blue-500"
                                  >
                                    <div className="flex items-center gap-2 min-w-0 flex-1">
                                      <span className="text-xs font-bold text-blue-600 dark:text-blue-400 w-5">
                                        #{idx + 1}
                                      </span>
                                      <ContribIcon className={`w-4 h-4 flex-shrink-0 ${getTypeColor(contributor.type)}`} />
                                      <span className="text-sm text-gray-700 dark:text-gray-300 truncate">
                                        {contributor.name}
                                      </span>
                                    </div>
                                    <span className="text-sm font-bold text-blue-700 dark:text-blue-300 ml-2">
                                      {new Intl.NumberFormat('en-US', {
                                        minimumFractionDigits: contribDigits,
                                        maximumFractionDigits: contribDigits,
                                      }).format(contributor.balance)}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          )}
        </div>
      </section>

      {/* Action Buttons */}
      <section className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm 
        ${isIOS ? 'rounded-2xl' : ''}`}>
        <div className="p-4">
          <h2 className="text-lg font-semibold mb-3 text-gray-900 dark:text-white flex items-center gap-2">
            <ArrowsRightLeftIcon className="w-5 h-5 text-blue-500" />
            {t('userDeepView.quickActions', 'Quick Actions')}
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {/* View Reports Button */}
            <button
              onClick={handleNavigateToReports}
              className={`flex flex-col items-center justify-center gap-1 p-3 rounded-lg transition-all
                bg-purple-50 dark:bg-purple-900/10 border border-purple-200 dark:border-purple-800
                hover:bg-purple-100 dark:hover:bg-purple-900/20
                ${isIOS ? 'active:scale-95' : ''}
              `}
            >
              <ChartBarIcon className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              <span className="text-xs font-medium text-gray-900 dark:text-white">
                {t('userDeepView.viewReports', 'Reports')}
              </span>
            </button>

            {/* Transaction History Button */}
            <button
              onClick={handleNavigateToTransactionHistory}
              className={`flex flex-col items-center justify-center gap-1 p-3 rounded-lg transition-all
                bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800
                hover:bg-blue-100 dark:hover:bg-blue-900/20
                ${isIOS ? 'active:scale-95' : ''}
              `}
            >
              <DocumentTextIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              <span className="text-xs font-medium text-gray-900 dark:text-white">
                {t('userDeepView.transactions', 'Transactions')}
              </span>
            </button>

            {/* Bet History Button - Only show for super-agent, agent, or player */}
            {(userData?.type === 'super-agent' || userData?.type === 'agent' || userData?.type === 'player') && (
              <button
                onClick={handleNavigateToBetHistory}
                className={`flex flex-col items-center justify-center gap-1 p-3 rounded-lg transition-all
                  bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800
                  hover:bg-amber-100 dark:hover:bg-amber-900/20
                  ${isIOS ? 'active:scale-95' : ''}
                `}
              >
                <CreditCardIcon className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                <span className="text-xs font-medium text-gray-900 dark:text-white">
                  {t('userDeepView.betHistory', 'Bet History')}
                </span>
              </button>
            )}

            {/* Change Password Button - Hidden for restricted users */}
            {!isRestricted && (
              <button
                onClick={() => setShowChangePasswordModal(true)}
                className={`flex flex-col items-center justify-center gap-1 p-3 rounded-lg transition-all
                  bg-indigo-50 dark:bg-indigo-900/10 border border-indigo-200 dark:border-indigo-800
                  hover:bg-indigo-100 dark:hover:bg-indigo-900/20
                  ${isIOS ? 'active:scale-95' : ''}
                `}
              >
                <KeyIcon className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                <span className="text-xs font-medium text-gray-900 dark:text-white">
                  {isCurrentUser 
                    ? t('userDeepView.changePass', 'Password')
                    : t('userDeepView.resetPass', 'Reset Pass')
                  }
                </span>
              </button>
            )}
          </div>

          {/* Transaction Type Buttons - Only show if not current user and not restricted */}
          {!isCurrentUser && !isRestricted && (
            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                {t('userDeepView.transactionOptions', 'Transaction Options')}
              </p>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => handleOpenTransaction('deposit')}
                  className={`flex items-center justify-center gap-2 px-4 py-3 rounded-xl transition-all
                    bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600
                    text-white font-medium shadow-sm hover:shadow-md
                    ${isIOS ? 'active:scale-95' : ''}
                  `}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  <span>{t('userDeepView.deposit', 'Deposit')}</span>
                </button>
                <button
                  onClick={() => handleOpenTransaction('withdraw')}
                  className={`flex items-center justify-center gap-2 px-4 py-3 rounded-xl transition-all
                    bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600
                    text-white font-medium shadow-sm hover:shadow-md
                    ${isIOS ? 'active:scale-95' : ''}
                  `}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                  </svg>
                  <span>{t('userDeepView.withdraw', 'Withdraw')}</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Currencies Section */}
      <section className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm 
        ${isIOS ? 'rounded-2xl' : ''}`}>
        <div className="p-4">
          <h2 className="text-lg font-semibold mb-3 text-gray-900 dark:text-white flex items-center gap-2">
            <BanknotesIcon className="w-5 h-5 text-yellow-500" />
            {isCurrentUser ? t('userDeepView.yourCurrencies', 'Your Currencies') : t('userDeepView.allowedCurrencies', 'Allowed Currencies')}
          </h2>
          <div className="space-y-4">
            <div className="flex flex-wrap gap-2">
              {isCurrentUser ? (
                // Only show user's own currencies
                userAttributes?.currencies?.map((currency) => (
                  <div
                    key={currency}
                    className={`inline-flex items-center px-3 py-1.5 rounded-full border border-blue-500 
                      bg-blue-50 text-blue-700 dark:border-blue-400 dark:bg-blue-900/50 dark:text-blue-300
                      ${isIOS ? 'shadow-sm' : ''}`}
                  >
                    <span className="text-sm font-medium">{currency}</span>
                  </div>
                ))
              ) : (
                // Show currency buttons for admins (only add, no remove)
                parentData?.attributes?.currencies?.map((currency) => {
                  const isSelected = userAttributes?.currencies?.includes(currency);
                  const isPlayer = userData?.type === USER_TYPES.PLAYER;
                  const hasExclusive = (userAttributes?.currencies || []).some(c =>
                    EXCLUSIVE_PLAYER_CURRENCIES.includes(c)
                  );
                  const blockedByPlayerRule =
                    isPlayer &&
                    !isSelected &&
                    EXCLUSIVE_PLAYER_CURRENCIES.includes(currency) &&
                    hasExclusive;
                  return (
                    <button
                      key={currency}
                      type="button"
                      onClick={() => handleCurrencyToggle(currency)}
                      className={`
                        inline-flex items-center px-4 py-2 rounded-full border-2 transition-all
                        ${isSelected
                          ? 'border-blue-500 bg-blue-50 text-blue-700 dark:border-blue-400 dark:bg-blue-900/50 dark:text-blue-300 cursor-not-allowed'
                          : blockedByPlayerRule
                            ? 'border-gray-200 bg-gray-50 text-gray-400 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-500 cursor-not-allowed opacity-60'
                            : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300 dark:hover:border-gray-500'}
                        ${isIOS ? 'shadow-sm active:scale-95 transition-transform' : ''}
                      `}
                      disabled={isSelected || blockedByPlayerRule}
                      title={
                        blockedByPlayerRule
                          ? t(
                              'userDeepView.errors.playerCurrencyExclusive',
                              'Players can only have one of USD, TND, TRY, IQD, or KWD. LBP can be added as a second currency.'
                            )
                          : undefined
                      }
                    >
                      <span className="text-base font-medium">{currency}</span>
                      {isSelected && (
                        <span className="ml-2 text-sm bg-blue-200 dark:bg-blue-800 text-blue-800 dark:text-blue-200 rounded-full w-5 h-5 inline-flex items-center justify-center">
                          ✓
                        </span>
                      )}
                    </button>
                  );
                })
              )}
            </div>
            {!isCurrentUser && userData?.type === USER_TYPES.PLAYER && (
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {t(
                  'userDeepView.playerCurrencyRule',
                  'Players can have only one of USD, TND, TRY, IQD, or KWD. LBP can be added as a second currency.'
                )}
              </p>
            )}
            {(!parentData?.attributes?.currencies || parentData.attributes.currencies.length === 0) && !isCurrentUser && (
              <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400">
                <ExclamationTriangleIcon className="w-5 h-5" />
                <p className="text-sm">{t('userDeepView.noCurrenciesFromParent', 'No currencies available from parent user')}</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Save Button - Only show for non-current users */}
      {!isCurrentUser && (
        <div className={`flex justify-end ${isIOS ? 'sticky bottom-0 -mx-4 px-4 py-4 bg-gray-50 dark:bg-gray-900 border-t dark:border-gray-800' : ''}`}>
          <button
            onClick={handleSave}
            disabled={isSaving || !hasChanges() || !isAttributesLoaded}
            className={`inline-flex items-center px-6 py-2.5 rounded-lg transition-colors
              ${!isAttributesLoaded 
                ? 'bg-gray-300 dark:bg-gray-600 text-gray-600 dark:text-gray-300 cursor-not-allowed'
                : hasChanges()
                  ? 'bg-green-500 hover:bg-green-600 text-white'
                  : 'bg-gray-300 dark:bg-gray-600 text-gray-600 dark:text-gray-300 cursor-not-allowed'}
              ${isIOS ? 'rounded-2xl shadow-lg active:scale-95 transition-transform w-full justify-center' : ''}
            `}
          >
            {isSaving ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {t('userDeepView.saving', 'Saving Changes...')}
              </>
            ) : !isAttributesLoaded ? (
              t('userDeepView.loadingAttributes', 'Loading Attributes...')
            ) : hasChanges() ? (
              <>
                <CheckCircleIcon className="w-5 h-5 mr-2" />
                {t('userDeepView.saveChanges', 'Save Changes')}
              </>
            ) : (
              t('userDeepView.noChanges', 'No Changes')
            )}
          </button>
        </div>
      )}

      {/* Block Confirmation Dialog */}
      {showBlockConfirm && !isCurrentUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className={`bg-white dark:bg-gray-800 p-6 max-w-md w-full shadow-xl
            ${isIOS ? 'rounded-2xl mx-4' : 'rounded-lg'}`}>
            <div className="flex items-center gap-3 text-red-600 dark:text-red-400 mb-4">
              <ExclamationTriangleIcon className="w-6 h-6" />
              <h3 className="text-lg font-semibold">{t('userDeepView.confirmBlock', 'Confirm Block User')}</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              {t('userDeepView.blockWarning', 'Are you sure you want to block this user? This will prevent them from accessing the system.')}
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowBlockConfirm(false)}
                className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
              >
                {t('common.cancel', 'Cancel')}
              </button>
              <button
                onClick={confirmBlock}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
              >
                {t('userDeepView.blockUser', 'Block User')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Currency Addition Confirmation Dialog */}
      {showCurrencyConfirm && !isCurrentUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className={`bg-white dark:bg-gray-800 p-6 max-w-md w-full shadow-xl
            ${isIOS ? 'rounded-2xl mx-4' : 'rounded-lg'}`}>
            <div className="flex items-center gap-3 text-blue-600 dark:text-blue-400 mb-4">
              <CurrencyDollarIcon className="w-6 h-6" />
              <h3 className="text-lg font-semibold">{t('userDeepView.confirmAddCurrency')}</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              {t('userDeepView.currencyWarning').replace('{currency}', pendingCurrency)}
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowCurrencyConfirm(false);
                  setPendingCurrency(null);
                }}
                className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
              >
                {t('common.cancel', 'Cancel')}
              </button>
              <button
                onClick={confirmAddCurrency}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                {t('userDeepView.addCurrency')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* IP Address Details Modal */}
      {showIPDetails && (ipToInspect || userAttributes?.lastIPAddress) && (
        <IPAddressDetails 
          ipAddress={ipToInspect || userAttributes.lastIPAddress}
          onClose={() => {
            setShowIPDetails(false);
            setIpToInspect(null);
          }}
        />
      )}

      {/* Transaction Modal */}
      {showTransactionModal && !isCurrentUser && (
        <TransactionModal
          isOpen={showTransactionModal}
          onClose={() => setShowTransactionModal(false)}
          onSubmit={handleTransactionSuccess}
          type={transactionType}
          preselectedUser={userData}
        />
      )}

      {/* Change Password Modal */}
      {showChangePasswordModal && (
        <ChangePasswordModal
          isOpen={showChangePasswordModal}
          onClose={() => setShowChangePasswordModal(false)}
          username={userData?.username}
        />
      )}
      </div>
    </div>
  );
};

export default UserDeepView; 